
import React, { forwardRef } from 'react';
import { MilestoneStatus, MainPackageContractNode, SubPackageContractNode } from '../types';
import { LinkNewSvg } from "@/components/svgs/plm"
import { Tooltip } from "antd"
import clsx from "clsx"

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const colors: Record<string, string> = {
    [MilestoneStatus.COMPLETED]: 'bg-green-100 text-green-700 border-green-200',
    [MilestoneStatus.PARTIAL]: 'bg-blue-100 text-blue-700 border-blue-200',
    [MilestoneStatus.PENDING]: 'bg-gray-100 text-gray-700 border-gray-200',
    [MilestoneStatus.OVERDUE]: 'bg-red-100 text-red-700 border-red-200',
  };

  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${colors[status]}`}>
      {status}
    </span>
  );
};

export const MainMilestoneCard = forwardRef<HTMLDivElement, { 
  value: MainPackageContractNode; 
  isActive: boolean;
  hasLink: boolean,
  onClick: () => void;
}>(({ value, isActive, hasLink, onClick }, ref) => {
  const progress = (value.percent || 0) * 100

  const contractRatio = ((Number(value.contractMilestoneRatio) || 0) * 100).toFixed(2)

  return (
    <div 
      ref={ref}
      onClick={onClick}
      className={`p-4 rounded-xl border-solid border-2 transition-all cursor-pointer ${
        isActive 
          ? 'bg-blue-50 border-blue-400 shadow-md' 
        : 'bg-white border-slate-200 hover:border-blue-300 hover:shadow-sm'
      }`}
    >
      <div className="flex justify-between items-start mb-2">
        <div className='flex items-center justify-center gap-2'>
          {hasLink && (
            <Tooltip title="关联节点">
                <div className={clsx("flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold",
                  isActive ? 'bg-blue-500 text-white' : 'bg-white text-gray-500 border-solid border border-gray-500'
                )}>
                <LinkNewSvg />
              </div>
            </Tooltip>
          )}
          <h4 className="font-bold text-slate-800 text-sm line-clamp-2">{value.description}</h4>
        </div>
        
        
        <StatusBadge status={value.status} />
      </div>
      
      <div className="space-y-2">
        <div className="flex justify-between text-xs text-slate-500">
          <span className="text-xs font-semibold inline-block text-blue-600">
            收款比例 {contractRatio}%
          </span>
          <span>截止日期: {value.date}</span>
        </div>
        
        <div className="relative pt-1">
          <div className='flex items-center justify-between mb-1'>
            <span className="text-[10px] font-semibold inline-block py-1 px-2 uppercase rounded-full text-blue-600 bg-blue-200">
              已收款: ¥{value.contractMilestoneReceived}
            </span>
            <span className="text-xs text-slate-500 inline-block">
              目标金额: ¥{value.contractMilestoneAmount}
            </span>
          </div>
          <div className="overflow-hidden h-2 mb-1 text-xs flex rounded bg-blue-200">
            <div style={{ width: `${progress}%` }} className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500 transition-all duration-500"></div>
          </div>
        </div>
      </div>
    </div>
  );
});

export const SubMilestoneCard: React.FC<{ value: SubPackageContractNode, isActive: boolean, hasLink: boolean, onLink: (val: SubPackageContractNode) => void }> = ({ value, isActive, hasLink, onLink }) => {
  const progress = (value.percent || 0) * 100 
  return (
    <div className={`p-4 border-solid border-2 rounded-xl hover:shadow-md transition-shadow ${
      isActive ? 'bg-purple-100 border-purple-400 shadow-md' : 'bg-white border-slate-200 shadow-sm'
    }`}>
      <div className="mb-2">
        <div className='flex justify-between items-start'>
          <div className="text-[10px] font-medium text-purple-600 uppercase tracking-wider flex items-center gap-1">
            {isActive ? (
              <Tooltip title="关联节点">
                <div className="flex items-center justify-center w-5 h-5 bg-purple-500 text-white rounded-full text-[10px] font-bold">
                  <LinkNewSvg />
                </div>
              </Tooltip>
            ) : (hasLink && (
              <Tooltip title="关联节点">
                <div onClick={() => onLink(value)} className="flex items-center justify-center w-5 h-5 border-solid border border-gray-500 text-gray-500 hover:bg-gray-200 cursor-pointer rounded-full text-[10px] font-bold">
                  <LinkNewSvg />
                </div>
              </Tooltip>
            ))}
            {value.purchasematter}
          </div>
          <div className="flex items-center gap-1">
            <StatusBadge status={value.status} />
            
          </div>
        </div>
        
        <h4 className="font-bold text-slate-800 text-sm">{value.paymentcondition}</h4>
      </div>

      <div className="space-y-2 mt-3">
        <div className="flex justify-between text-xs text-slate-500">
          <span></span>
          <span>截止日期: {value.predictpaytime}</span>
        </div>
        <div className="flex justify-between text-xs text-slate-500">
          <span className="font-semibold text-slate-700">已付金额: ¥{value.paymentConditionReceived}</span>
          <span>目标金额: ¥{value.paymentConditionAmount}</span>
          
        </div>
        <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
          <div 
            className="bg-purple-500 h-full transition-all duration-500" 
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>
    </div>
  );
};
